import { LoadingButton } from '@mui/lab';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Link from '@mui/material/Link';
import LoginIcon from '@mui/icons-material/Login';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { alpha } from '@mui/material/styles';
import { MuiOtpInput } from 'mui-one-time-password-input';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Copyright } from '../../components/Copyright';
import COLORS from '../../constants/colors';
import { formFields } from '../../forms/fields/formFields';
import { getLoginFormSchema } from '../../forms/validationSchema/formSchema';
import { parseApiError } from '../../helpers/api';
import { useLoginMutation } from '../../redux/features/auth/auth.apiSlice';
import { setCredentials } from '../../redux/features/auth/auth.slice';
import { useAppDispatch } from '../../redux/hooks';
import { locations } from '../../routes/locations';
import { LoginResponse } from '../../types/auth';
import packageJson from '../../../package.json';

const OTP_LENGTH = 4;

const HAIRLINE = alpha(COLORS.MAIN_DARK, 0.08);

/** Drop the artwork here; the panel degrades to a plain gradient without it. */
const LOGIN_ILLUSTRATION = '/images/login-illustration.png';

export function Login() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  const [login, { isLoading, error }] = useLoginMutation();

  const [isOtpRequired, setIsOtpRequired] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formSchema, setFormSchema] = useState(getLoginFormSchema(false));
  const { handleSubmit, values, handleChange, touched, errors, setValues } = useFormik({
    initialValues: {
      [formFields.email]: '',
      [formFields.password]: '',
      [formFields.otp]: '',
    },
    validationSchema: formSchema,
    initialTouched: { [formFields.email]: true, [formFields.password]: true },
    onSubmit: async (formValues) => {
      try {
        const res: LoginResponse = await login({
          ...formValues,
          email: formValues.email.trim(),
        }).unwrap();

        dispatch(setCredentials(res));

        if (res?.otpRequired) {
          setIsOtpRequired(res.otpRequired);
          setFormSchema(getLoginFormSchema(res.otpRequired));
        }

        if (res?.accessToken) {
          navigate((state as { from?: string })?.from || locations.dashboard(), { replace: true });
        }
      } catch (err) {
        await setValues({ ...values, otp: '' });
      }
    },
  });

  const onForgotPassword = () => {
    navigate(locations.forgotPassword());
  };

  const validateOtp = (value) => !Number.isNaN(Number.parseInt(value, 10));

  const handleChangeOtp = async (value: string) => {
    await setValues({ ...values, otp: value });

    if (value.length === OTP_LENGTH) {
      handleSubmit();
    }
  };

  const renderLoginForm = () => (
    <>
      <TextField
        id="email"
        fullWidth
        name={formFields.email}
        label="Email"
        value={values[formFields.email]}
        onChange={handleChange}
        error={touched.email && !!errors.email}
        helperText={touched.email && errors.email}
        autoComplete="username"
        margin="normal"
        required
        autoFocus
      />
      <TextField
        margin="normal"
        required
        fullWidth
        id="password"
        name={formFields.password}
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={values[formFields.password]}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          handleChange({
            target: {
              name: formFields.password,
              type: 'password',
              value: event.target.value.replace(/\s/g, ''),
            },
          });
        }}
        error={touched.password && !!errors.password}
        helperText={touched.password && errors.password}
        autoComplete="current-password"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword((visible) => !visible)}
                edge="end"
                size="small"
              >
                {showPassword ? (
                  <VisibilityOffIcon sx={{ fontSize: '20px' }} />
                ) : (
                  <VisibilityIcon sx={{ fontSize: '20px' }} />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </>
  );

  const renderOtpForm = () => (
    <FormControl fullWidth margin="normal">
      <MuiOtpInput value={values.otp} length={OTP_LENGTH} onChange={handleChangeOtp} validateChar={validateOtp} />
      {touched.otp && !!errors.otp && <FormHelperText error>{errors.otp}</FormHelperText>}
    </FormControl>
  );

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex' }}>
      {/* Brand panel. Hidden below md so a phone gets the form, not a hero image. */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: '1 1 55%',
          alignItems: 'center',
          justifyContent: 'center',
          p: '48px',
          // No divider: the gradient-to-white change already separates the columns.
          background: `linear-gradient(160deg, ${COLORS.X_LIGHT_CYAN} 0%, ${COLORS.WHITE} 100%)`,
        }}
      >
        <Box
          component="img"
          src={LOGIN_ILLUSTRATION}
          alt="Madkhol mobile app preview"
          // The panel should stay a clean gradient if the asset is ever missing.
          onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
            const image = event.currentTarget;
            image.hidden = true;
          }}
          sx={{ width: '100%', maxWidth: '700px', height: 'auto', objectFit: 'contain' }}
        />
      </Box>

      <Box
        sx={{
          flex: '1 1 45%',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: '16px',
          py: '48px',
          backgroundColor: COLORS.WHITE,
          position: 'relative',
        }}
      >
        <Box
          component="button"
          type="button"
          aria-label="Go to home"
          onClick={() => navigate(locations.home())}
          sx={{
            display: 'block',
            m: 0,
            mb: '28px',
            p: 0,
            border: 0,
            background: 'none',
            cursor: 'pointer',
            '&:focus-visible': {
              outline: `2px solid ${COLORS.PRIMARY_BLUE}`,
              outlineOffset: '4px',
              borderRadius: '4px',
            },
          }}
        >
          <img
            style={{ display: 'block', width: '180px', height: 'auto', objectFit: 'contain' }}
            src={isArabic ? '/images/logo-with-text-ar.png' : '/images/logo-with-text-en.png'}
            alt="madkhol logo"
          />
        </Box>

        <Paper
          elevation={0}
          component="main"
          sx={{
            width: '100%',
            maxWidth: '420px',
            p: { xs: '24px', sm: '32px' },
            borderRadius: '16px',
            backgroundColor: COLORS.WHITE,
            // On the split layout the brand panel already frames the form, so the
            // card sheds its own frame; below md it is the only surface and keeps it.
            border: { xs: `1px solid ${HAIRLINE}`, md: 'none' },
            boxShadow: { xs: `0px 8px 24px ${alpha(COLORS.MAIN_DARK, 0.06)}`, md: 'none' },
          }}
        >
          <Typography
            component="h1"
            variant="h5"
            sx={{ fontWeight: 700, color: COLORS.MAIN_DARK, textAlign: 'center' }}
          >
            {isOtpRequired ? 'Two-factor authentication' : 'Sign in'}
          </Typography>
          <Typography
            sx={{ mt: '6px', mb: '8px', fontSize: '0.875rem', color: COLORS.LIGHT_GRAY, textAlign: 'center' }}
          >
            {isOtpRequired
              ? `Enter the ${OTP_LENGTH}-digit verification code to continue`
              : 'Use your Madkhol admin account to continue'}
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            {isOtpRequired ? renderOtpForm() : renderLoginForm()}
            {error && (
              <Alert severity="error" sx={{ mt: '16px', borderRadius: '8px' }}>
                {parseApiError(error)?.data?.message || 'Sign in failed. Please check your details and try again.'}
              </Alert>
            )}
            <LoadingButton
              type="submit"
              color="primary"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: '24px' }}
              endIcon={<LoginIcon />}
              loading={isLoading}
              loadingPosition="end"
            >
              Sign In
            </LoadingButton>
            <Box sx={{ mt: '16px', textAlign: 'center' }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={onForgotPassword}
                sx={{ fontWeight: 600, cursor: 'pointer' }}
              >
                Forgot password?
              </Link>
            </Box>
          </Box>
        </Paper>

        {/* Anchored to the column so it reads as a footer, not as trailing form text. */}
        <Box sx={{ position: 'absolute', bottom: '24px', left: 0, right: 0, px: '16px' }}>
          <Copyright />
          <Typography sx={{ mt: '4px', fontSize: '0.75rem', color: COLORS.LIGHT_GRAY, textAlign: 'center' }}>
            Version {packageJson.version}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
